import type { FormOrderStep, OrderFormValues } from '../typings'

export const computeDirtyFields = (
  initial: OrderFormValues,
  updated: OrderFormValues,
): string[] => {
  const dirtyFields: string[] = []

  const normalize = (value: unknown): unknown => {
    if (value === undefined || value === null || value === '') return ''
    if (typeof value === 'number') return value.toString()
    return value
  }

  const hasChanged = (initialValue: unknown, updatedValue: unknown): boolean =>
    JSON.stringify(normalize(initialValue)) !== JSON.stringify(normalize(updatedValue))

  // A pickup/delivery step's destination is EITHER a connected depot OR a free
  // location: the server persists only one of them (`locationOrDepotId`). Its
  // stable identity is the depot id when connected, otherwise the coordinates +
  // address - derived fields like `adjustedLatLng` or `source` are ignored so the
  // editor rewriting them doesn't read as a change.
  const getDestinationKey = (step: FormOrderStep | undefined): string => {
    if (step?.depotId) return `depot:${step.depotId}`
    const location = step?.location
    if (!location?.latLng) return ''
    return `location:${JSON.stringify(location.latLng)}:${location.address ?? ''}`
  }

  const compareFields = (initialObj: any, updatedObj: any, prefix = '') => {
    Object.keys(updatedObj).forEach(key => {
      const fieldPath = prefix ? `${prefix}.${key}` : key
      const initialValue = initialObj?.[key]
      const updatedValue = updatedObj[key]

      // Special handling for 'type' field - always add as top-level if changed
      if (key === 'type' && !prefix) {
        if (initialValue !== updatedValue) {
          dirtyFields.push('type')
        }
        return
      }

      if (
        typeof updatedValue === 'object' &&
        updatedValue !== null &&
        !Array.isArray(updatedValue) &&
        !prefix && // Only recurse for top-level objects to get 2-level paths
        ['order', 'pickup', 'delivery'].includes(key) // Only recurse into these specific objects
      ) {
        // Recurse into nested objects for 2-level paths
        compareFields(initialValue || {}, updatedValue, key)
      } else if (prefix) {
        // `depotId` and `location` are not independent on an order step: the server
        // stores only `locationOrDepotId` (see getOrderStepPatch). Compare them as a
        // single destination so re-selecting the same depot - which rebuilds the
        // location object and toggles depotId - is not flagged as a phantom change.
        // Otherwise we dispatch an update the server treats as a no-op, no change
        // set is produced and the save spinner hangs forever.
        if ((prefix === 'pickup' || prefix === 'delivery') && key === 'depotId') return

        if ((prefix === 'pickup' || prefix === 'delivery') && key === 'location') {
          if (getDestinationKey(initialObj) !== getDestinationKey(updatedObj)) {
            dirtyFields.push(fieldPath)
          }
          return
        }

        // Add nested fields (with prefix) for comparison
        if (hasChanged(initialValue, updatedValue)) {
          dirtyFields.push(fieldPath)
        }
      } else if (!prefix && !['order', 'pickup', 'delivery'].includes(key)) {
        // Handle other top-level fields that aren't objects we recurse into
        if (hasChanged(initialValue, updatedValue)) {
          dirtyFields.push(fieldPath)
        }
      }
    })
  }

  compareFields(initial, updated)
  return dirtyFields
}
